import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { EMPTY_FILTER, type TaskFilter } from '@/logic/filters'

// Per-device preferences, kept in localStorage and never synced: the last
// view, the one set of filters, the Plan tab's layout for each screen size,
// the Events setting, which tray groups are open, and collapsed weekdays.

export type ViewKey = 'plan' | 'calendar'

export type PlanLayout = 'columns' | 'stacked'

/** How Google events show in each day's heading: a count to open, always open, or not at all. */
export type EventsMode = 'count' | 'all' | 'off'

export type TrayKey = 'routines' | 'done' | 'notdone' | 'nodate'

interface PrefsState {
  view: ViewKey
  filter: TaskFilter
  /** Until chosen, phones use Stacked and wide screens Columns. */
  layoutPhone: PlanLayout
  layoutWide: PlanLayout
  events: EventsMode
  openGroups: TrayKey[]
  /** On a wide screen, whether the tray column is open rather than a strip of counts. */
  trayColumnOpen: boolean
  /** ISO weekdays (1 Monday to 7 Sunday) folded shut, remembered by weekday so the choice carries over to every week. */
  collapsedWeekdays: number[]
  setView: (v: ViewKey) => void
  setFilter: (patch: Partial<TaskFilter>) => void
  clearFilter: () => void
  setLayout: (wide: boolean, layout: PlanLayout) => void
  setEvents: (mode: EventsMode) => void
  toggleGroup: (key: TrayKey, single: boolean) => void
  openGroup: (key: TrayKey, single: boolean) => void
  closeGroups: () => void
  setTrayColumnOpen: (open: boolean) => void
  toggleWeekday: (weekday: number) => void
  openWeekday: (weekday: number) => void
}

export const usePrefs = create<PrefsState>()(
  persist(
    (set) => ({
      view: 'plan',
      filter: EMPTY_FILTER,
      layoutPhone: 'stacked',
      layoutWide: 'columns',
      events: 'count',
      openGroups: [],
      trayColumnOpen: false,
      collapsedWeekdays: [],
      setView: (view) => set({ view }),
      setFilter: (patch) => set((s) => ({ filter: { ...s.filter, ...patch } })),
      clearFilter: () => set({ filter: EMPTY_FILTER }),
      setLayout: (wide, layout) =>
        set((s) => (wide ? { layoutWide: layout } : { layoutPhone: layout, openGroups: layout === 'columns' ? s.openGroups.slice(0, 1) : s.openGroups })),
      setEvents: (events) => set({ events }),
      // phone Columns shows one group at a time, to keep the days in view
      toggleGroup: (key, single) =>
        set((s) => ({ openGroups: s.openGroups.includes(key) ? s.openGroups.filter((k) => k !== key) : single ? [key] : [...s.openGroups, key] })),
      openGroup: (key, single) => set((s) => ({ openGroups: s.openGroups.includes(key) ? s.openGroups : single ? [key] : [...s.openGroups, key] })),
      closeGroups: () => set({ openGroups: [] }),
      setTrayColumnOpen: (trayColumnOpen) => set({ trayColumnOpen }),
      toggleWeekday: (wd) =>
        set((s) => ({ collapsedWeekdays: s.collapsedWeekdays.includes(wd) ? s.collapsedWeekdays.filter((d) => d !== wd) : [...s.collapsedWeekdays, wd] })),
      openWeekday: (wd) => set((s) => ({ collapsedWeekdays: s.collapsedWeekdays.filter((d) => d !== wd) })),
    }),
    {
      name: 'cuplzap.prefs',
      version: 2,
      // version 1 knew List, Board, the list's grouping and a search filter; all of that is gone
      migrate: (old, version) => {
        const prev = (old ?? {}) as Record<string, unknown>
        if (version < 2) return { view: prev.view === 'calendar' ? 'calendar' : 'plan' } as Partial<PrefsState>
        return prev as Partial<PrefsState>
      },
    },
  ),
)
